import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL

// Mismos colores por nivel que ya se usan en Dashboard.jsx, para que el
// VideoForos se sienta parte de la misma plataforma (en vez de copiar
// los morados de EspañolGo).
const COLORES_NIVEL = {
  PRE: '#94A3B8', A1: '#22C55E', A2: '#3B82F6', B1: '#8B5CF6',
  B2: '#F59E0B', C1: '#EF4444', C2: '#1B3A8C'
}

const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado']
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

function formatFecha(fechaStr) {
  const d = new Date(fechaStr + 'T00:00:00')
  return DIAS[d.getDay()] + ' ' + d.getDate() + ' de ' + MESES[d.getMonth()]
}

function formatHora(horaStr) {
  if (!horaStr) return ''
  const [h, m] = horaStr.split(':')
  const hora = parseInt(h)
  const ampm = hora >= 12 ? 'PM' : 'AM'
  const h12 = hora % 12 || 12
  return h12 + ':' + m + ' ' + ampm
}

/* Pagina de "VideoForos": reuniones grupales en vivo (Google Meet) sobre
   un tema puntual, moderadas por un profesor — mismo concepto que ya
   existe en EspañolGo, portado con el estilo visual de EnglishBridge
   (ver api/src/routes/videoforos.js para el backend). */
export default function VideoForos({ usuario }) {
  const [foros, setForos] = useState([])
  const [inscritos, setInscritos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [procesando, setProcesando] = useState(null)
  const [mensaje, setMensaje] = useState(null)
  const [filtroNivel, setFiltroNivel] = useState('todos')

  const cargarDatos = useCallback(() => {
    setCargando(true)
    Promise.all([
      axios.get(`${API}/videoforos`),
      usuario?.id
        ? axios.get(`${API}/videoforos/mis-inscripciones/${usuario.id}`).catch(() => ({ data: { datos: [] } }))
        : Promise.resolve({ data: { datos: [] } })
    ]).then(([forosRes, inscritosRes]) => {
      setForos(forosRes.data.datos || [])
      setInscritos(inscritosRes.data.datos || [])
    }).catch(() => setForos([]))
      .finally(() => setCargando(false))
  }, [usuario])

  useEffect(() => { cargarDatos() }, [cargarDatos])

  useEffect(() => {
    if (!mensaje) return
    const t = setTimeout(() => setMensaje(null), 4000)
    return () => clearTimeout(t)
  }, [mensaje])

  async function inscribirse(foroId) {
    if (!usuario?.id) {
      setMensaje({ tipo: 'error', texto: 'Necesitas iniciar sesion para inscribirte a un VideoForo.' })
      return
    }
    setProcesando(foroId)
    try {
      const res = await axios.post(`${API}/videoforos/${foroId}/inscribir`, { usuario_id: usuario.id })
      setInscritos(prev => [...prev, foroId])
      setMensaje({ tipo: 'ok', texto: res.data.mensaje })
      cargarDatos()
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'Error al inscribirse' })
    } finally {
      setProcesando(null)
    }
  }

  async function cancelar(foroId) {
    if (!usuario?.id) return
    setProcesando(foroId)
    try {
      await axios.delete(`${API}/videoforos/${foroId}/cancelar`, { data: { usuario_id: usuario.id } })
      setInscritos(prev => prev.filter(id => id !== foroId))
      setMensaje({ tipo: 'ok', texto: 'Inscripcion cancelada' })
      cargarDatos()
    } catch (err) {
      setMensaje({ tipo: 'error', texto: 'Error al cancelar' })
    } finally {
      setProcesando(null)
    }
  }

  const niveles = ['todos', ...new Set(foros.map(f => f.nivel_id).filter(Boolean))]
  const forosFiltrados = filtroNivel === 'todos' ? foros : foros.filter(f => f.nivel_id === filtroNivel)
  const porFecha = forosFiltrados.reduce((acc, f) => {
    if (!acc[f.fecha]) acc[f.fecha] = []
    acc[f.fecha].push(f)
    return acc
  }, {})

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', animation: 'fadeIn .3s ease both' }}>

      <div style={{ background: 'linear-gradient(135deg,#0D1F52,#1B3A8C)', borderRadius: 16, padding: 24, marginBottom: 24, color: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <span style={{ fontSize: 32 }}>🎙</span>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 2 }}>VideoForos</h1>
            <p style={{ fontSize: 13, opacity: .85 }}>Reuniones en vivo para practicar tu ingles hablado con otros estudiantes, sobre un tema puntual.</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          {[
            { ic: '👥', label: 'Grupos pequeños' },
            { ic: '🎯', label: 'Tema semanal' },
            { ic: '🌎', label: 'Google Meet' },
            { ic: '🧑‍🏫', label: 'Con moderador' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, opacity: .9 }}>
              <span>{item.ic}</span><span>{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {mensaje && (
        <div style={{ background: mensaje.tipo === 'ok' ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${mensaje.tipo === 'ok' ? '#22C55E' : '#EF4444'}`, borderRadius: 10, padding: '10px 16px', marginBottom: 20, fontSize: 13, color: mensaje.tipo === 'ok' ? '#166534' : '#991B1B' }}>
          {mensaje.tipo === 'ok' ? '✅ ' : '⚠ '}{mensaje.texto}
        </div>
      )}

      {niveles.length > 1 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {niveles.map(n => (
            <button key={n} onClick={() => setFiltroNivel(n)}
              style={{ padding: '6px 16px', borderRadius: 999, border: '2px solid ' + (filtroNivel === n ? (COLORES_NIVEL[n] || '#1B3A8C') : '#E2E6F0'),
                background: filtroNivel === n ? (COLORES_NIVEL[n] || '#1B3A8C') : '#fff',
                color: filtroNivel === n ? '#fff' : '#374151',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {n === 'todos' ? 'Todos los niveles' : n}
            </button>
          ))}
        </div>
      )}

      {cargando ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Cargando VideoForos...</div>
      ) : (
        <>
          {Object.keys(porFecha).sort().map(fecha => (
            <div key={fecha} style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ background: '#1B3A8C', color: '#fff', borderRadius: 10, padding: '6px 14px', fontSize: 13, fontWeight: 700 }}>
                  🗓 {formatFecha(fecha)}
                </div>
                <div style={{ flex: 1, height: 1, background: '#E2E6F0' }} />
                <span style={{ fontSize: 12, color: '#6B7280' }}>{porFecha[fecha].length} sesion{porFecha[fecha].length > 1 ? 'es' : ''}</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {porFecha[fecha].map(foro => {
                  const estaInscrito = inscritos.includes(foro.id)
                  const sinCupos = foro.cupos_disponibles <= 0
                  const cargandoEste = procesando === foro.id

                  return (
                    <div key={foro.id}
                      style={{ background: '#fff', border: '1.5px solid ' + (estaInscrito ? '#1B3A8C' : '#E2E6F0'),
                        borderRadius: 14, padding: '18px 20px',
                        boxShadow: estaInscrito ? '0 0 0 3px rgba(27,58,140,0.1)' : 'none' }}>

                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>

                        <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#E8EFFE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                          {foro.profesor?.avatar_emoji || '🧑‍🏫'}
                        </div>

                        <div style={{ flex: 1, minWidth: 200 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                            {foro.nivel_id && (
                              <span style={{ background: COLORES_NIVEL[foro.nivel_id] || '#6B7280', color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 999 }}>
                                {foro.nivel_id}
                              </span>
                            )}
                            {foro.tema && (
                              <span style={{ fontSize: 11, background: '#F0F4FF', color: '#1B3A8C', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>
                                {foro.tema}
                              </span>
                            )}
                            {estaInscrito && <span style={{ fontSize: 11, color: '#1B3A8C', fontWeight: 700 }}>✓ Inscrito</span>}
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: '#1A1A2E', marginBottom: 4 }}>{foro.titulo}</div>
                          {foro.descripcion && <div style={{ fontSize: 12, color: '#6B7280' }}>{foro.descripcion}</div>}
                          {foro.profesor?.nombre && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>Moderador: {foro.profesor.nombre}</div>}
                        </div>

                        <div style={{ textAlign: 'center', minWidth: 80 }}>
                          <div style={{ fontSize: 17, fontWeight: 800, color: '#1B3A8C' }}>{formatHora(foro.hora_inicio)}</div>
                          {foro.hora_fin && <div style={{ fontSize: 11, color: '#6B7280' }}>{formatHora(foro.hora_fin)}</div>}
                          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{foro.duracion_min || 75} min</div>
                        </div>

                        <div style={{ textAlign: 'center', minWidth: 70 }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color: sinCupos ? '#EF4444' : foro.cupos_disponibles <= 3 ? '#D4AF37' : '#22C55E' }}>
                            {foro.cupos_disponibles}
                          </div>
                          <div style={{ fontSize: 11, color: '#6B7280' }}>de {foro.cupo_maximo}</div>
                          <div style={{ width: 50, height: 4, background: '#E2E6F0', borderRadius: 999, margin: '4px auto 0', overflow: 'hidden' }}>
                            <div style={{ width: (foro.cupos_ocupados / foro.cupo_maximo * 100) + '%', height: '100%', borderRadius: 999,
                              background: sinCupos ? '#EF4444' : foro.cupos_disponibles <= 3 ? '#D4AF37' : '#1B3A8C' }} />
                          </div>
                        </div>

                        <div style={{ minWidth: 140, display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {estaInscrito ? (
                            <>
                              {foro.link_meet && (
                                <a href={foro.link_meet} target="_blank" rel="noopener noreferrer"
                                  style={{ width: '100%', boxSizing: 'border-box', textAlign: 'center', padding: '7px 0', background: '#1B3A8C', color: '#fff', border: 'none', borderRadius: 10, fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'none' }}>
                                  🎙 Unirse
                                </a>
                              )}
                              <button onClick={() => cancelar(foro.id)} disabled={cargandoEste}
                                style={{ width: '100%', padding: '7px 0', background: '#FEF2F2', color: '#EF4444', border: '1.5px solid #EF4444', borderRadius: 10, fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                {cargandoEste ? '...' : 'Cancelar'}
                              </button>
                            </>
                          ) : (
                            <button onClick={() => inscribirse(foro.id)} disabled={sinCupos || cargandoEste}
                              style={{ width: '100%', padding: '9px 0', background: sinCupos ? '#E2E6F0' : '#1B3A8C', color: sinCupos ? '#9CA3AF' : '#fff', border: 'none', borderRadius: 10, fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: sinCupos ? 'not-allowed' : 'pointer' }}>
                              {cargandoEste ? 'Procesando...' : sinCupos ? 'Sin cupos' : 'Inscribirme'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}

          {forosFiltrados.length === 0 && (
            <div style={{ textAlign: 'center', padding: 60, color: '#6B7280', background: '#fff', border: '1px solid #E2E6F0', borderRadius: 14 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🎙</div>
              <p style={{ fontSize: 16 }}>No hay VideoForos disponibles para este nivel.</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
